import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { Loader2, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/institutions/invitations/${token}/preview`);
        setPreview(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Invitation invalid");
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    if (!user) {
      navigate(`/signup?invitation=${token}&email=${encodeURIComponent(preview?.email || "")}`);
      return;
    }
    if ((user?.email || "").toLowerCase() !== preview?.email?.toLowerCase()) {
      toast.error(`Please sign in as ${preview.email}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/institutions/invitations/${token}/accept`);
      toast.success("Joined!");
      navigate("/institution");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Center>
        <p>Missing invitation token.</p>
      </Center>
    );
  }

  if (loadingPreview || loading) {
    return (
      <Center>
        <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
      </Center>
    );
  }

  if (!preview) {
    return (
      <Center>
        <p className="text-muted-foreground">This invitation is invalid or has been used.</p>
      </Center>
    );
  }

  return (
    <div className="min-h-screen tb-auth-bg grid place-items-center p-6" data-testid="accept-invitation-page">
      <div className="w-full max-w-md tb-glass rounded-3xl p-8">
        <Logo size="md" />
        <div className="mt-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-100/70 dark:bg-violet-500/15 px-3 py-1 text-xs font-medium text-violet-700 dark:text-violet-200">
            <Building2 className="h-3.5 w-3.5" /> Institution invitation
          </div>
          <h1 className="mt-4 font-display text-2xl tracking-tight font-medium">
            Join {preview.institution_name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground capitalize">
            {preview.institution_type} · You&apos;ll join as <span className="font-medium text-foreground">{preview.role}</span>
          </p>

          <p className="mt-6 text-sm">
            This invitation is for <span className="font-medium">{preview.email}</span>.
          </p>

          {!user ? (
            <div className="mt-6 space-y-2">
              <Button
                onClick={accept}
                className="w-full h-11 rounded-full"
                data-testid="accept-create-account"
              >
                Create your account & join
              </Button>
              <Button
                variant="outline"
                className="w-full h-11 rounded-full"
                onClick={() => navigate(`/login?next=${encodeURIComponent(`/accept-invitation?token=${token}`)}`)}
              >
                I already have an account
              </Button>
            </div>
          ) : (user?.email || "").toLowerCase() !== preview.email?.toLowerCase() ? (
            <div className="mt-6">
              <p className="text-sm text-rose-600">
                You&apos;re signed in as <span className="font-medium">{user.email}</span>. Please sign in as {preview.email} to accept this invitation.
              </p>
            </div>
          ) : (
            <Button
              onClick={accept}
              disabled={busy}
              className="mt-6 w-full h-11 rounded-full"
              data-testid="accept-invitation-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> Accept invitation</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="tb-glass rounded-3xl px-8 py-6">{children}</div>
    </div>
  );
}
